// Calendar days and wall-clock times in the club's time zone, Intl-based so the same code runs in Bun
// and the browser. Instants travel as ISO strings (UTC); calendar days as 'YYYY-MM-DD'.

export type IsoDate = string // '2026-10-07'

const partsCache = new Map<string, Intl.DateTimeFormat>()
function parts(timeZone: string) {
  let f = partsCache.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
    partsCache.set(timeZone, f)
  }
  return f
}

/** Wall-clock fields of an instant in a zone. */
export function zonedParts(instant: Date | string, timeZone: string) {
  const d = typeof instant === 'string' ? new Date(instant) : instant
  const p = Object.fromEntries(parts(timeZone).formatToParts(d).map((x) => [x.type, x.value]))
  return {
    year: Number(p.year),
    month: Number(p.month),
    day: Number(p.day),
    hour: Number(p.hour),
    minute: Number(p.minute),
    second: Number(p.second),
  }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Calendar day of an instant in the zone ('2026-10-31' for 23:30 local even if UTC is already Nov 1). */
export function localDate(instant: Date | string, timeZone: string): IsoDate {
  const p = zonedParts(instant, timeZone)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

/** 'HH:MM' of an instant in the zone. */
export function localTime(instant: Date | string, timeZone: string): string {
  const p = zonedParts(instant, timeZone)
  return `${pad(p.hour)}:${pad(p.minute)}`
}

/** Today's calendar day in the zone. */
export function today(timeZone: string, now: Date = new Date()): IsoDate {
  return localDate(now, timeZone)
}

/**
 * The instant at which the wall clock in `timeZone` shows `date` + `time`.
 * Resolves DST gaps forward and ambiguities to the earlier offset, like Temporal's 'compatible'.
 */
export function zonedToInstant(date: IsoDate, time: string, timeZone: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const [hh, mm] = time.split(':').map(Number) as [number, number]
  const asUtc = Date.UTC(y, m - 1, d, hh, mm)
  // Two passes converge for every real-world zone (offset changes at most once near the target).
  let guess = asUtc
  for (let i = 0; i < 2; i++) {
    const p = zonedParts(new Date(guess), timeZone)
    const shown = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute)
    guess += asUtc - shown
  }
  return new Date(guess)
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const t = new Date(Date.UTC(y, m - 1, d + days))
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`
}

/** ISO weekday 1 (Mon) … 7 (Sun) of a calendar day. */
export function isoWeekday(date: IsoDate): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return wd === 0 ? 7 : wd
}

export function monthStart(date: IsoDate): IsoDate {
  return date.slice(0, 8) + '01'
}

/** First day of the following month. */
export function nextMonth(date: IsoDate): IsoDate {
  const [y, m] = date.split('-').map(Number) as [number, number]
  return m === 12 ? `${y + 1}-01-01` : `${y}-${pad(m + 1)}-01`
}

export function daysBetween(from: IsoDate, to: IsoDate): number {
  const a = Date.parse(from + 'T00:00:00Z')
  const b = Date.parse(to + 'T00:00:00Z')
  return Math.round((b - a) / 86_400_000)
}

/** Postgres daterange literal, half-open: '[from,to)'; open end when to is null. */
export function dateRange(from: IsoDate, to: IsoDate | null): string {
  return `[${from},${to ?? ''})`
}

/** Parse a Postgres daterange '[2025-07-01,2026-07-01)' into inclusive-exclusive bounds. */
export function parseDateRange(range: string): { from: IsoDate | null; to: IsoDate | null } {
  const m = /^[[(]([^,]*),([^\])]*)[\])]$/.exec(range)
  if (!m) return { from: null, to: null }
  return { from: m[1] || null, to: m[2] || null }
}
